import type { CardOrientation, typeCard } from '@susu/custom-made/components/card/card';
import { Card, CardInfoIconType, CardSize } from '@susu/custom-made/components/card/card';
import { useViewSize, ViewSize } from '@susu/custom-made/hooks/useViewSize';
import { ThemeContext } from '@susu/custom-made/providers';
import { imageDefinition } from '@susu/custom-made/utils/imageDefinition';
import { imageUrlBuilder } from '@susu/custom-made/utils/imageUrlProvider/imageUrlProvider';
import type { MonogramPositions, Option, OptionValue, typeOptionValue } from '@susu/custom-made/utils/interfaces';
import { getImagePaths, getImageUrl, getThumbnails } from '@susu/custom-made/utils/utils';
import cn from 'classnames';
import type { RefObject } from 'react';
import { useContext } from 'react';

import type { cardItemsType } from './cardList';
import styles from './cardListOption.module.scss';
import { ConfigurationTypes } from '@susu/custom-made/utils/productDefinition';
import { isOptionHiddenByRule, isOptionValueHiddenByRule } from '@susu/custom-made/utils/businessRules';
import { useMissingValues } from '@susu/custom-made/hooks/useMissingValues';

export default function CardListOption({
  cardItems,
  cardSubItems,
  selectedItemId,
  cardOrientation,
  isGroup,
  isAdvancedExpanded,
  optionValues,
  isPresentationOnly,
  isDetailedView,
  cardClick = () => {},
  selectedOption,
  cardItemRef,
  affectedConfigurationTypeId,
}: Readonly<{
  cardItems: cardItemsType;
  cardSubItems?: (string | number)[];
  selectedItemId?: number | null;
  cardOrientation?: CardOrientation;
  isGroup?: boolean;
  isAdvancedExpanded: boolean;
  optionValues?: typeOptionValue;
  isPresentationOnly?: boolean;
  isDetailedView?: boolean;
  cardClick?: (card: typeCard) => void;
  selectedOption?: Option | null;
  cardItemRef?: RefObject<HTMLDivElement>;
  affectedConfigurationTypeId?: ConfigurationTypes;
}>) {
  const { state } = useContext(ThemeContext);
  const viewSize = useViewSize();
  const { isOptionWithoutSelection } = useMissingValues();

  const isOptionWithMissingSelection = (item: Option) => {
    let isOptionWithMissingSelection: boolean = false;
    const { monogramsIdsWithMissingSelection } = state;

    if (item.type === 'OPTION' && isOptionWithoutSelection(item, affectedConfigurationTypeId)) {
      isOptionWithMissingSelection = true;
    }

    if (item.type === 'OPTION_GROUP' && item.options) {
      isOptionWithMissingSelection = item.options.some(option =>
        isOptionWithoutSelection(option, affectedConfigurationTypeId),
      );
    }

    if (item.type === 'MONOGRAM' && monogramsIdsWithMissingSelection) {
      isOptionWithMissingSelection = monogramsIdsWithMissingSelection.length > 0;
    }

    return isOptionWithMissingSelection;
  };

  const getSelectedOption = (item: Option) => {
    const selectedOption = item.options
      ? (affectedConfigurationTypeId && state.selectedOptions[affectedConfigurationTypeId][item.options[0].id]) ||
        item.options[0].optionValues![0]
      : (affectedConfigurationTypeId && state.selectedOptions[affectedConfigurationTypeId][item.id]) ||
        item.optionValues![0];

    return {
      name: optionValues![selectedOption].name,
      selectedOption: selectedOption,
    };
  };

  const getOptionCardData = (item: Option) => {
    let imagePath;
    let subText = '';
    if (item.type === 'MONOGRAM') {
      imagePath = getImageUrl(item.imageUrlTemplate, item.imageName, state.selectedFabric?.layerImageName);
      if (!Object.keys(state.selectedMonogram).length) {
        subText = 'None';
      } else {
        subText = Object.values(state.selectedMonogram)[0].initials;
      }
    } else {
      const { name, selectedOption } = getSelectedOption(item);
      subText = name;

      imagePath = getImageUrl(
        optionValues![selectedOption].imageUrlTemplate,
        optionValues![selectedOption].imageName,
        state.selectedFabric?.layerImageName,
      );
    }

    const { imageThumbnail, fallbackImageThumbnail } = getThumbnails(imagePath, true);

    return {
      subText,
      imageUrl: imageThumbnail,
      fallbackImageUrl: fallbackImageThumbnail,
    };
  };

  const getSubText = (selectedOption: Option | undefined | null, item: OptionValue) => {
    if (selectedOption?.showDescription) {
      return item.description;
    } else if (selectedOption?.appendCode) {
      return item.code;
    } else {
      return undefined;
    }
  };

  const getSelectedMonograms = (item: Option) => {
    if (!item.positions) {
      return [];
    }
    const monogramList: MonogramPositions[] = [];
    Object.values(item.positions).forEach(position => {
      if (state.selectedMonogram[position.id]) {
        monogramList.push(position);
      }
    });

    return monogramList;
  };

  return (
    <>
      {isGroup &&
        cardItems &&
        (cardItems as Option[]).map((cardItem: Option, index: number) => {
          const item = cardItem;
          if (item && !isOptionHiddenByRule(item.id, state.hiddenOptionsAndValues, affectedConfigurationTypeId)) {
            const foundPositions: MonogramPositions[] = getSelectedMonograms(item);

            if (item.type === 'MONOGRAM' && isPresentationOnly && isDetailedView && foundPositions.length) {
              return foundPositions.map(position => {
                const subText =
                  state.selectedMonogram[position.id].initials +
                  ' • ' +
                  item.colors![state.selectedMonogram[position.id].colorId!].name +
                  ' • ' +
                  item.fontTypes![state.selectedMonogram[position.id].fontStyleId!].name;

                const isMissing = isOptionWithMissingSelection(item);
                const imageUrls = getImageUrl(
                  position.imageUrlTemplate,
                  position.imageName,
                  state.selectedFabric?.layerImageName!,
                );

                return (
                  <div
                    key={index + '_' + position.id}
                    className={cn(
                      {
                        [styles.isAdvanced]: item.isAdvanced,
                      },
                      { [styles.isAdvancedExpanded]: isAdvancedExpanded },
                    )}
                  >
                    <Card
                      card={item}
                      cardImages={[
                        imageUrlBuilder({
                          imageUrls: [imageUrls.imageUrl],
                          imageDefinition: imageDefinition.thumbnail,
                          noTransformation: true,
                        }).src,
                      ]}
                      cardText={position.name}
                      isSelected={false}
                      tabIndex={index + 1}
                      cardClick={cardClick}
                      hideMobileInfoText={false}
                      cardSize={CardSize.Medium}
                      isParent={true}
                      cardOrientation={cardOrientation}
                      cardSubText={subText}
                      isPresentationOnly={isPresentationOnly}
                      showWarning={isMissing}
                      cardFallbackImageUrl={
                        imageUrlBuilder({
                          imageUrls: [imageUrls.fallbackUrl],
                          imageDefinition: imageDefinition.thumbnail,
                          noTransformation: true,
                        }).src
                      }
                    />
                  </div>
                );
              });
            } else {
              const { subText, imageUrl, fallbackImageUrl } = getOptionCardData(item);
              const isMissing = isOptionWithMissingSelection(item);
              return (
                <div
                  key={index}
                  className={cn(
                    {
                      [styles.isAdvanced]: item.isAdvanced,
                    },
                    { [styles.isAdvancedExpanded]: isAdvancedExpanded },
                  )}
                  ref={cardItemRef}
                >
                  <Card
                    card={item}
                    cardImages={[imageUrl]}
                    cardText={item.name}
                    isSelected={false}
                    tabIndex={index + 1}
                    cardClick={cardClick}
                    hideMobileInfoText={false}
                    cardSize={CardSize.Medium}
                    isParent={true}
                    cardOrientation={cardOrientation}
                    cardSubText={subText}
                    isPresentationOnly={isPresentationOnly}
                    showWarning={isMissing}
                    cardFallbackImageUrl={fallbackImageUrl}
                  />
                </div>
              );
            }
          }
        })}
      {!isGroup &&
        cardSubItems &&
        (cardSubItems as number[]).map((cardSubItem: number, index: number) => {
          const item: OptionValue = (cardItems as typeOptionValue)[cardSubItem];

          if (item && !isOptionValueHiddenByRule(item.id, state.hiddenOptionsAndValues, affectedConfigurationTypeId)) {
            const { imageThumbnail, fallbackImageThumbnail } = getImagePaths(
              item.imageUrlTemplate,
              item.imageName,
              state.selectedFabric?.layerImageName!,
              true,
            );

            return (
              <Card
                key={index}
                card={item}
                cardImages={[imageThumbnail]}
                cardText={item.name}
                isSelected={selectedItemId ? selectedItemId === item.id : false}
                tabIndex={index + 1}
                cardClick={cardClick}
                cardSize={CardSize.Medium}
                isParent={false}
                cardOrientation={cardOrientation}
                cardInfoIconType={CardInfoIconType.Light}
                cardSubText={getSubText(selectedOption, item)}
                showTooltip={viewSize === ViewSize.Mobile || viewSize === ViewSize.Tablet}
                cardPrice={
                  item.additionalPrice
                    ? '+' + item.additionalPrice.symbol + item.additionalPrice.amount.toString()
                    : undefined
                }
                cardFallbackImageUrl={fallbackImageThumbnail}
              />
            );
          }
        })}
    </>
  );
}
